/**
 * Listing CSV Processor
 *
 * Unlike contacts/properties/units (which are PMS-export shaped and need a
 * per-provider transformer), listings use our OWN canonical column schema.
 * So there is a single column map here — no PMSSelector, no PMS transformer.
 *
 * The validated output type is the canonical `ListingCsvRow` from
 * `@super/shared-types` (the same type the API validates against) — this file
 * only introduces a loose, internal `RawListingRow` for the pre-validation
 * parse stage (CSV cells are all strings until coerced).
 *
 * IMPORTANT — fail-loudly behaviour: the base processor converts row-validation
 * failures into *warnings* and silently skips the row. That contradicts the
 * requirement that bad rows fail loudly rather than silently corrupting the
 * listings database. This processor overrides `processCSVResults` so a row with
 * missing/invalid required fields is a HARD error (`success === false`),
 * blocking the upload until the file is fixed.
 */

import type { CSVRow, BaseUploadContext } from '@/types/csv-upload'
import {
  ListingCsvRowSchema,
  ListingType,
  type ListingCsvRow,
} from '@super/shared-types'
import Papa from 'papaparse'
import {
  BaseCSVProcessor,
  type BaseCSVProcessingOptions,
  type BaseCSVResult,
} from './base-csv-processor'

/**
 * Loose, pre-validation shape produced by the transform step: every canonical
 * field, but with raw/coercible values (strings from the CSV, numbers that may
 * be NaN). Keyed off `ListingCsvRow` so a schema field rename breaks compile.
 */
type RawListingRow = {
  [K in keyof ListingCsvRow]?: string | number | boolean | undefined
}

interface ListingUploadContext extends BaseUploadContext {
  companyId: string
}

export interface ListingCSVProcessingResult extends BaseCSVResult {
  /** Validated rows, in the canonical shape the API accepts. */
  listings: ListingCsvRow[]
  sampleRows?: CSVRow[]
  headers?: string[]
  duplicateCount?: number
}

/**
 * Canonical listing CSV columns, in template order. The first alias of each
 * field is the one written into the downloadable template.
 */
const COLUMN_ALIASES: Record<keyof ListingCsvRow, string[]> = {
  address: ['address', 'property address', 'street address'],
  unit: ['unit', 'unit number', 'apt', 'apartment'],
  monthlyRentDollars: [
    'monthly_rent',
    'monthly rent',
    'rent',
    'price',
    'market rent',
  ],
  type: ['type', 'listing type', 'property type'],
  beds: ['beds', 'bedrooms', 'bed'],
  baths: ['baths', 'bathrooms', 'bath'],
  sqft: ['sqft', 'square feet', 'size'],
  isAvailable: ['is_available', 'available', 'availability'],
  availabilityDate: [
    'availability_date',
    'available date',
    'date available',
    'availabilitydate',
  ],
  detailsUrl: ['details_url', 'details url', 'listing url', 'url'],
  showingUrl: ['showing_url', 'showing url', 'showing link'],
  imgUrl: ['img_url', 'image url', 'image', 'photo'],
  petPolicy: ['pet_policy', 'pet policy'],
  dogsAllowed: ['dogs_allowed', 'dogs allowed', 'dogs'],
  catsAllowed: ['cats_allowed', 'cats allowed', 'cats'],
  hasParking: ['has_parking', 'parking'],
  housingVouchersAccepted: [
    'housing_vouchers_accepted',
    'housing vouchers',
    'housing voucher',
    'section8_accepted',
    'section 8',
    'section8',
    'voucher',
  ],
  housingVouchersPolicy: ['housing_vouchers_policy', 'housing vouchers policy'],
  shortDescription: ['short_description', 'short description', 'summary'],
  longDescription: ['long_description', 'long description', 'description'],
  externalId: ['external_id', 'id', 'listing id'],
}

const normalizeHeader = (header: string): string =>
  header.trim().toLowerCase().replace(/\s+/g, ' ')

/**
 * Parse "$1,250" / "1250" → 1250; empty → undefined. Unparseable → NaN so the
 * schema rejects it (only relevant for the required numeric fields).
 */
function parseNumeric(raw: string | undefined): number | undefined {
  if (raw === undefined) return undefined
  const trimmed = raw.trim()
  if (trimmed === '') return undefined
  const cleaned = trimmed.replace(/[$,]/g, '')
  const value = Number(cleaned)
  return Number.isNaN(value) ? NaN : value
}

/** Like parseNumeric but drops unparseable values (for optional fields). */
function parseOptionalNumeric(raw: string | undefined): number | undefined {
  const value = parseNumeric(raw)
  return value !== undefined && Number.isNaN(value) ? undefined : value
}

const VALID_TYPES = new Set<string>(Object.values(ListingType))

/** Keep the value only if it's a known ListingType; otherwise drop it. */
function normalizeType(raw: string | undefined): string | undefined {
  const upper = raw?.toUpperCase()
  return upper && VALID_TYPES.has(upper) ? upper : undefined
}

/** Keep the value only if it parses to a real date; otherwise drop it. */
function normalizeDate(raw: string | undefined): string | undefined {
  if (!raw) return undefined
  return Number.isNaN(new Date(raw).getTime()) ? undefined : raw
}

/** Parse yes/no/true/false/1/0/y/n → boolean; empty/unknown → undefined. */
function parseBoolean(raw: string | undefined): boolean | undefined {
  if (raw === undefined) return undefined
  const v = raw.trim().toLowerCase()
  if (v === '') return undefined
  if (['true', 'yes', 'y', '1'].includes(v)) return true
  if (['false', 'no', 'n', '0'].includes(v)) return false
  return undefined
}

/** Stable dedupe key: detailsUrl if present, else address + unit. */
function listingDedupeKey(record: ListingCsvRow): string {
  if (record.detailsUrl) return `url:${record.detailsUrl.toLowerCase()}`
  return `addr:${record.address.toLowerCase()}:${(record.unit ?? '').toLowerCase()}`
}

export class ListingCSVProcessor extends BaseCSVProcessor<
  RawListingRow,
  ListingCSVProcessingResult,
  ListingUploadContext
> {
  constructor(companyId: string, options: BaseCSVProcessingOptions = {}) {
    if (!companyId?.trim()) {
      throw new Error('Company ID is required')
    }
    super({ companyId }, options)
  }

  /** Build a normalized-key lookup for a raw CSV row. */
  private normalizeRow(row: CSVRow): Record<string, string | undefined> {
    const normalized: Record<string, string | undefined> = {}
    for (const [key, value] of Object.entries(row)) {
      normalized[normalizeHeader(key)] = value
    }
    return normalized
  }

  private pick(
    normalizedRow: Record<string, string | undefined>,
    field: keyof ListingCsvRow,
  ): string | undefined {
    for (const alias of COLUMN_ALIASES[field]) {
      const value = normalizedRow[normalizeHeader(alias)]
      if (value !== undefined && value.trim() !== '') return value.trim()
    }
    return undefined
  }

  protected transformRow(
    row: CSVRow,
    _rowNumber: number,
  ): RawListingRow | null {
    const nr = this.normalizeRow(row)

    // A row with no address and no rent is treated as blank → skip silently.
    const address = this.pick(nr, 'address')
    const rentRaw = this.pick(nr, 'monthlyRentDollars')
    if (!address && !rentRaw) {
      this.context.silentSkip = true
      return null
    }

    return {
      address: address ?? '',
      unit: this.pick(nr, 'unit'),
      monthlyRentDollars: parseNumeric(rentRaw),
      // Optional fields are accepted leniently — unstorable values are dropped.
      type: normalizeType(this.pick(nr, 'type')),
      beds: parseNumeric(this.pick(nr, 'beds')),
      baths: parseNumeric(this.pick(nr, 'baths')),
      sqft: parseOptionalNumeric(this.pick(nr, 'sqft')),
      isAvailable: parseBoolean(this.pick(nr, 'isAvailable')),
      availabilityDate: normalizeDate(this.pick(nr, 'availabilityDate')),
      detailsUrl: this.pick(nr, 'detailsUrl'),
      showingUrl: this.pick(nr, 'showingUrl'),
      imgUrl: this.pick(nr, 'imgUrl'),
      petPolicy: this.pick(nr, 'petPolicy'),
      dogsAllowed: parseBoolean(this.pick(nr, 'dogsAllowed')),
      catsAllowed: parseBoolean(this.pick(nr, 'catsAllowed')),
      hasParking: parseBoolean(this.pick(nr, 'hasParking')),
      housingVouchersAccepted: parseBoolean(
        this.pick(nr, 'housingVouchersAccepted'),
      ),
      housingVouchersPolicy: this.pick(nr, 'housingVouchersPolicy'),
      shortDescription: this.pick(nr, 'shortDescription'),
      longDescription: this.pick(nr, 'longDescription'),
      externalId: this.pick(nr, 'externalId'),
    }
  }

  /**
   * Validate a raw row against the SAME schema the API enforces (single source
   * of truth), returning the coerced canonical row on success so callers never
   * re-parse.
   */
  protected validateRecord(
    record: RawListingRow,
    _rowNumber: number,
  ): {
    isValid: boolean
    errors: string[]
    warnings: string[]
    parsed?: ListingCsvRow
  } {
    const result = ListingCsvRowSchema.safeParse(record)
    if (result.success) {
      return { isValid: true, errors: [], warnings: [], parsed: result.data }
    }
    return {
      isValid: false,
      errors: result.error.issues.map(issue => issue.message),
      warnings: [],
    }
  }

  protected createResult(data: {
    success: boolean
    totalRows: number
    processedRows: number
    skipped: number
    records: RawListingRow[]
    errors: BaseCSVResult['errors']
    warnings: BaseCSVResult['warnings']
    sampleRows: CSVRow[]
    headers: string[]
    memoryUsage: { used: number; limit: number; percentage: number }
  }): ListingCSVProcessingResult {
    // `listings` is populated with the validated canonical rows by
    // processCSVResults; the loose `records` are not carried onto the result.
    return {
      success: data.success,
      totalRows: data.totalRows,
      processedRows: data.processedRows,
      skipped: data.skipped,
      listings: [],
      errors: data.errors,
      warnings: data.warnings,
      sampleRows: data.sampleRows,
      headers: data.headers,
      memoryUsage: data.memoryUsage,
    }
  }

  protected createErrorResult(
    errors: BaseCSVResult['errors'],
    memoryStatus: { used: number; limit: number; percentage: number },
  ): ListingCSVProcessingResult {
    return {
      success: false,
      totalRows: 0,
      processedRows: 0,
      skipped: 0,
      listings: [],
      errors,
      warnings: [],
      sampleRows: [],
      headers: [],
      memoryUsage: memoryStatus,
    }
  }

  /**
   * Override of the base row loop that treats validation failures as HARD
   * errors (fail loudly) instead of warnings, and dedupes within the file
   * (last row wins for a given key, matching the server upsert key).
   */
  protected processCSVResults(
    results: Papa.ParseResult<CSVRow>,
  ): ListingCSVProcessingResult {
    const errors: BaseCSVResult['errors'] = []
    const warnings: BaseCSVResult['warnings'] = []
    const validRows: ListingCsvRow[] = []
    let skipped = 0

    const skipRows = this.options.skipRows || 0
    const dataRows = results.data.slice(skipRows)

    dataRows.forEach((row, index) => {
      const rowNumber = index + 1 + skipRows

      if (index % 100 === 0) {
        const memoryStatus = this.memoryMonitor.checkMemory()
        if (!memoryStatus.safe) {
          errors.push({
            row: rowNumber,
            message:
              memoryStatus.message || 'Memory limit exceeded during processing',
          })
          return
        }
      }

      try {
        const record = this.transformRow(row, rowNumber)
        if (!record) {
          // Blank row → skip silently (silentSkip set by transformRow).
          this.context.silentSkip = undefined
          skipped++
          return
        }

        const validation = this.validateRecord(record, rowNumber)
        if (validation.isValid && validation.parsed) {
          validRows.push(validation.parsed)
          validation.warnings.forEach(message =>
            warnings.push({ row: rowNumber, message, data: row }),
          )
        } else {
          // HARD errors — blocks the upload.
          validation.errors.forEach(message =>
            errors.push({ row: rowNumber, message, data: row }),
          )
        }
      } catch (error) {
        errors.push({
          row: rowNumber,
          message:
            error instanceof Error
              ? error.message
              : 'Unknown error during transformation',
          data: row,
        })
      }
    })

    // Surface papaparse's own parse errors.
    results.errors.forEach(error => {
      errors.push({
        row: error.row !== undefined ? error.row + 1 : 0,
        message: error.message,
        data: error,
      })
    })

    // Dedupe within the file: last row wins for a given key.
    const byKey = new Map<string, ListingCsvRow>()
    let duplicateCount = 0
    for (const record of validRows) {
      const key = listingDedupeKey(record)
      if (byKey.has(key)) duplicateCount++
      byKey.set(key, record)
    }
    const deduped = Array.from(byKey.values())
    if (duplicateCount > 0) {
      warnings.push({
        row: 0,
        message: `${duplicateCount} duplicate listing${duplicateCount === 1 ? '' : 's'} found in file (same URL or address+unit) — the last occurrence was kept.`,
      })
    }

    const finalMemoryStatus = this.memoryMonitor.checkMemory()

    const result = this.createResult({
      success: errors.length === 0 && deduped.length > 0,
      totalRows: results.data.length,
      processedRows: deduped.length,
      skipped,
      records: [],
      errors,
      warnings,
      sampleRows: results.data.slice(0, 5),
      headers: results.meta.fields || [],
      memoryUsage: {
        used: finalMemoryStatus.used,
        limit: finalMemoryStatus.limit,
        percentage: finalMemoryStatus.percentage,
      },
    })
    result.listings = deduped
    result.duplicateCount = duplicateCount
    return result
  }
}

/** Header row for the downloadable listing CSV template (canonical names). */
const LISTING_CSV_TEMPLATE_HEADERS: string[] = (
  Object.keys(COLUMN_ALIASES) as Array<keyof ListingCsvRow>
).map(field => COLUMN_ALIASES[field][0])

/** Build a small example CSV the user can download and fill in. */
export function buildListingCsvTemplate(): string {
  const exampleRow: Record<string, string> = {
    address: '123 Main St',
    unit: 'A',
    monthly_rent: '1500',
    type: 'APARTMENT',
    beds: '2',
    baths: '1',
    sqft: '850',
    is_available: 'true',
    availability_date: '2026-08-01',
    details_url: 'https://example.com/listings/123-main-a',
    showing_url: 'https://example.com/showings/123-main-a',
    img_url: 'https://example.com/images/123-main-a.jpg',
    pet_policy: 'Cats and small dogs allowed',
    housing_vouchers_accepted: 'true',
    housing_vouchers_policy:
      'Housing vouchers welcome for qualified applicants',
    dogs_allowed: 'true',
    cats_allowed: 'true',
    has_parking: 'true',
    short_description: 'Bright 2 bed near downtown',
    long_description: 'Spacious 2 bedroom apartment with updated kitchen.',
    external_id: 'EXT-123-A',
  }
  return Papa.unparse({
    fields: LISTING_CSV_TEMPLATE_HEADERS,
    data: [LISTING_CSV_TEMPLATE_HEADERS.map(h => exampleRow[h] ?? '')],
  })
}
